import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  InboundPhoneNumber,
  StrategyWithVersions,
  InboundCallStrategyVersionCreate,
} from "@/lib/types";
import { format } from "date-fns";
import { useStrategiesStore } from "@/stores/strategiesStore";

interface InboundCallSettingsProps {
  organizationId: string;
}

export default function InboundCallSettings({
  organizationId,
}: InboundCallSettingsProps) {
  const [inboundPhoneNumbers, setInboundPhoneNumbers] = useState<
    InboundPhoneNumber[]
  >([]);
  const [selectedStrategies, setSelectedStrategies] = useState<
    Record<string, string>
  >({});
  const [selectedVersions, setSelectedVersions] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const [savingPhoneId, setSavingPhoneId] = useState<string | null>(null);

  // Prevent initialization from running multiple times
  const initialized = useRef(false);

  // Get strategies from the store - using the whole object to prevent rerender issues
  const strategiesStore = useStrategiesStore();
  const strategies = strategiesStore.strategiesByOrg[organizationId] || [];

  // Fetch phone numbers only once on initial render
  useEffect(() => {
    if (!organizationId) return;

    const loadData = async () => {
      try {
        // Fetch phone numbers
        const phoneNumbers = await api.getOrganizationInboundPhoneNumbers(
          organizationId
        );
        setInboundPhoneNumbers(phoneNumbers);

        // Fetch strategies if not already loaded
        if (!strategies.length) {
          await strategiesStore.fetchStrategiesForCurrentOrg();
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast({
          title: "Failed to load data",
          description: String(error),
          variant: "destructive",
        });
      }
    };

    loadData();
  }, [organizationId]); // Only depend on organizationId

  // Initialize the selected values only once after data is loaded
  useEffect(() => {
    if (
      !inboundPhoneNumbers.length ||
      !strategies.length ||
      initialized.current
    ) {
      return;
    }

    const newSelectedVersions: Record<string, string> = {};
    const newSelectedStrategies: Record<string, string> = {};

    inboundPhoneNumbers.forEach((phone) => {
      if (phone.strategy_version_id) {
        newSelectedVersions[phone.id] = phone.strategy_version_id;

        // Find which strategy this version belongs to
        for (const strategy of strategies) {
          const matchingVersion = strategy.versions.find(
            (v) => v.id === phone.strategy_version_id
          );

          if (matchingVersion) {
            newSelectedStrategies[phone.id] = strategy.id;
            break;
          }
        }
      }
    });

    setSelectedVersions(newSelectedVersions);
    setSelectedStrategies(newSelectedStrategies);
    initialized.current = true;
  }, [inboundPhoneNumbers, strategies]);

  const handleStrategyChange = useCallback(
    (phoneNumberId: string, strategyId: string) => {
      setSelectedStrategies((prev) => ({
        ...prev,
        [phoneNumberId]: strategyId,
      }));

      // When changing the strategy, reset the version selection for this phone number
      setSelectedVersions((prev) => ({
        ...prev,
        [phoneNumberId]: "",
      }));
    },
    []
  );

  const handleVersionChange = useCallback(
    (phoneNumberId: string, versionId: string) => {
      setSelectedVersions((prev) => ({
        ...prev,
        [phoneNumberId]: versionId,
      }));
    },
    []
  );

  const handleSave = useCallback(
    async (phoneNumberId: string) => {
      try {
        setSavingPhoneId(phoneNumberId);
        setLoading(true);

        const versionId = selectedVersions[phoneNumberId];
        if (!versionId) {
          toast({
            title: "Please select a strategy version",
            variant: "destructive",
          });
          return;
        }

        // Find the phone number object to get the actual phone number string
        const phoneNumberObj = inboundPhoneNumbers.find(
          (p) => p.id === phoneNumberId
        );
        if (!phoneNumberObj) {
          toast({
            title: "Phone number not found",
            variant: "destructive",
          });
          return;
        }

        const data: InboundCallStrategyVersionCreate = {
          phone_number: phoneNumberObj.phone_number,
          strategy_version_id: versionId,
        };

        await api.createInboundCallStrategyVersion(data);

        toast({
          title: "Successfully updated inbound call strategy",
        });

        // Update the current phone number object with the new strategy_version_id
        setInboundPhoneNumbers((prev) =>
          prev.map((phone) =>
            phone.id === phoneNumberId
              ? { ...phone, strategy_version_id: versionId }
              : phone
          )
        );
      } catch (error) {
        console.error("Failed to save inbound call strategy version:", error);
        toast({
          title: "Failed to save inbound call strategy",
          description: String(error),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
        setSavingPhoneId(null);
      }
    },
    [inboundPhoneNumbers, selectedVersions]
  );

  const getStrategyName = useCallback(
    (strategyVersionId: string): string => {
      for (const strategy of strategies) {
        const version = strategy.versions.find(
          (v) => v.id === strategyVersionId
        );
        if (version) {
          return strategy.name;
        }
      }
      return "Unknown Strategy";
    },
    [strategies]
  );

  const getVersionDescription = useCallback(
    (strategyVersionId: string): string => {
      for (const strategy of strategies) {
        const version = strategy.versions.find(
          (v) => v.id === strategyVersionId
        );
        if (version) {
          return version.version_description || `Version ${version.id}`;
        }
      }
      return "Unknown Version";
    },
    [strategies]
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Inbound Call Configuration</h2>
      </div>

      {inboundPhoneNumbers.length === 0 ? (
        <div className="bg-gray-50 p-4 rounded-md text-center">
          No inbound phone numbers available for this organization.
        </div>
      ) : (
        <Table className="bg-white border border-gray-200 rounded-md">
          <TableHeader>
            <TableRow>
              <TableHead>Phone Number</TableHead>
              <TableHead>Strategy</TableHead>
              <TableHead>Current Strategy Version</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {inboundPhoneNumbers.map((phone) => (
              <TableRow key={phone.id}>
                <TableCell>{phone.phone_number}</TableCell>
                <TableCell>
                  <Select
                    value={selectedStrategies[phone.id] || ""}
                    onValueChange={(value) =>
                      handleStrategyChange(phone.id, value)
                    }
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select a strategy" />
                    </SelectTrigger>
                    <SelectContent>
                      {strategies.length === 0 ? (
                        <div className="text-gray-500 text-sm p-3">
                          No strategies available
                        </div>
                      ) : (
                        strategies.map((strategy) => (
                          <SelectItem key={strategy.id} value={strategy.id}>
                            {strategy.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    value={selectedVersions[phone.id] || ""}
                    onValueChange={(value) =>
                      handleVersionChange(phone.id, value)
                    }
                    disabled={!selectedStrategies[phone.id]}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select a version" />
                    </SelectTrigger>
                    <SelectContent>
                      {!selectedStrategies[phone.id] ? (
                        <div className="text-gray-500 text-sm p-3">
                          Select a strategy first
                        </div>
                      ) : (
                        strategies
                          .find((s) => s.id === selectedStrategies[phone.id])
                          ?.versions.map((version) => (
                            <SelectItem key={version.id} value={version.id}>
                              {version.version_description ||
                                `Version ${version.id}`}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleSave(phone.id)}
                    disabled={
                      !selectedVersions[phone.id] ||
                      phone.strategy_version_id ===
                        selectedVersions[phone.id] ||
                      loading ||
                      savingPhoneId === phone.id
                    }
                  >
                    {savingPhoneId === phone.id ? "Saving..." : "Save"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
